import axios, { type AxiosRequestConfig } from 'axios'
import { useCookies } from '@vueuse/integrations/useCookies'
import createAuthRefreshInterceptor from 'axios-auth-refresh'
import { get, includes } from 'lodash'
import axiosRetry from 'axios-retry'
import ENV from '@/constants/env'
import useApi from '@/composables/api'
import { ACCESS_TOKEN_LIFETIME, COOKIE_OPTIONS } from '@/constants/cookies'
import { isPublicSubdomain } from '@/functions/utils'
import type { LoginResponse } from '../client'

interface FailedRequest {
    response: {
        config: AxiosRequestConfig
    }
}

const apiHost = ENV.apiHost
const cookies = useCookies()

const axiosInstance = axios.create({
    baseURL: apiHost,
    headers: { 'Content-Type': 'application/json' }
})

axiosRetry(axiosInstance, { retries: 2, retryCondition: error => error.response?.status === 502 })

axiosInstance.interceptors.request.use(config => {
    const token = cookies.get(`access_token`)

    if (token) {
        config.headers.Authorization = `Bearer ${token}`
    }

    return config
})

axiosInstance.interceptors.response.use(
    response => response,
    error => {
        const { logout } = useApi()

        if (includes([417, 418], error.response?.status)) {
            logout().then(() => {
                window.open(`/login/logout`, `_self`)
            })
        }

        // Handle 429 Too many requests errors.
        if (error.response?.status === 429) {
            window.open(`/errors/429`, `_self`)
        }

        return Promise.reject(error)
    }
)

export const refreshAuthLogic = (failedRequest: FailedRequest): Promise<void> => {
    const { logout } = useApi()

    if (isPublicSubdomain()) {
        return Promise.reject()
    }

    const accessToken = cookies.get(`access_token`)
    const sessionKey = cookies.get(`session_key`)

    if (!accessToken && !sessionKey) {
        logout().then(() => {
            window.open(`/login/logout`, `_self`)
        })
        return Promise.reject()
    }

    // Persist the renewed token with the same lifetime it is given at sign-in. Storing it with
    // COOKIE_OPTIONS alone omits `expires`, which turns it into a session cookie. That is
    // invisible in a desktop browser, but iOS discards session cookies whenever it terminates the
    // wrapper app, which left the app with no access token on every cold start and dependent on a
    // single un-retried request to get one back.
    const storeAccessToken = (token: string) => {
        cookies.set(`access_token`, token, {
            ...COOKIE_OPTIONS,
            expires: new Date(Date.now() + ACCESS_TOKEN_LIFETIME)
        })

        if (failedRequest.response.config.headers) {
            failedRequest.response.config.headers['Authorization'] = `Bearer ${token}`
        }
    }

    const applyRenewedToken = (response: { data: LoginResponse }) => {
        const newAccessToken = get(response, `data.access_token`)

        if (!newAccessToken) {
            return Promise.reject()
        }

        storeAccessToken(newAccessToken)
        return Promise.resolve()
    }

    // The API answering 401/403 means it has judged the credential invalid. Anything else, such as
    // a gateway error, a timeout, or a request that never completed, tells us nothing about the
    // session, so the credentials must survive for the next attempt.
    const isRejectedByApi = (error: unknown) => includes([401, 403], get(error, `response.status`))

    /**
     * Trades a session_key for a Laravel access token, for the one case where no access token is
     * present at all.
     *
     * There are two `/auth/sessionkey` endpoints and they point in opposite directions. This
     * unauthenticated POST is the legacy one: session_key is a core concept rather than an
     * official Laravel credential, and the direction of travel is away from treating it as one.
     * The preferred direction is its counterpart, the authenticated `GET /auth/sessionkey` in
     * `composables/api.ts`, which converts an access token into a session_key.
     *
     * Deliberately **not** used to recover from a failed `/auth/refresh`. How long a token stays
     * refreshable is governed by the refresh window on the API (`JWT_REFRESH_TTL`), so extending a
     * session means changing that rather than exchanging credentials here. This endpoint survives
     * only because Command Center impersonation still needs it, and is expected to go away once CX
     * moves to Helm.
     */
    const exchangeSessionKey = () =>
        axios({
            method: `POST`,
            url: `${apiHost}/auth/sessionkey`,
            data: {
                session_key: sessionKey
            }
        })

    const endSession = (error: unknown) => {
        if (isRejectedByApi(error)) {
            logout(`${window.location.pathname}${window.location.search}`)
        }

        return Promise.reject()
    }

    if (!accessToken) {
        return exchangeSessionKey().then(applyRenewedToken).catch(endSession)
    }

    return axios({
        method: `POST`,
        url: `${apiHost}/auth/refresh`,
        headers: {
            Authorization: `Bearer ${accessToken}`
        }
    })
        .then(applyRenewedToken)
        .catch(endSession)
}

createAuthRefreshInterceptor(axiosInstance, refreshAuthLogic)

export default axiosInstance
